import { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { isTokenExpiringSoon } from '@/lib/auth'
import { isTokenExpired } from '@/lib/jwt'
import { DefaultLayout } from '@/components/layout/AppLayout'
import { Skeleton } from '@/components/ui/Skeleton'

/**
 * Auth-protected layout wrapper.
 *
 * - Waits for Zustand persist to rehydrate from localStorage before making auth decisions
 * - Proactively refreshes token if expiring within 5 minutes
 * - Redirects to /login with current path if not authenticated
 * - Renders DefaultLayout (sidebar + header + animated Outlet) if authenticated
 *
 * BUG FIX: Previous version had a race condition where Zustand persist had not
 * rehydrated yet on first render, causing isAuthenticated=false + token=null,
 * which immediately redirected to /login. Now we explicitly wait for rehydration
 * using the onRehydrateStorage callback + hasHydrated flag.
 */
// Rutas que un usuario con permisos :own puede abrir (su práctica). Todo lo demás → redirige.
const SCOPED_ALLOWED_PREFIXES = [
  '/crm/files', '/crm/appointments', '/crm/reports', '/crm/migrations',
  '/crm/profile', '/change-password',
]
function isScopedAllowed(pathname: string): boolean {
  return SCOPED_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

export function AuthLayout() {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const mustChangePassword = useAuthStore((s) => s.mustChangePassword)
  const refreshAuth = useAuthStore((s) => s.refreshAuth)
  const permissions = useAuthStore((s) => s.admin?.permissions) || []
  const isScopedUser = ['crm:appointments:own', 'crm:files:own', 'crm:reports:own'].some((p) => permissions.includes(p))
  const [isVerifying, setIsVerifying] = useState(true)
  const [hasHydrated, setHasHydrated] = useState(false)

  // Wait for Zustand persist to rehydrate from localStorage
  useEffect(() => {
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHasHydrated(true)
    })

    // If already hydrated (e.g. navigation within the same session)
    if (useAuthStore.persist.hasHydrated()) {
      setHasHydrated(true)
    }

    return () => unsub()
  }, [])

  // Verify auth once store has hydrated
  useEffect(() => {
    if (!hasHydrated) return

    async function verify() {
      const currentToken = useAuthStore.getState().token
      const refreshToken = useAuthStore.getState().refreshToken
      const currentAuth = useAuthStore.getState().isAuthenticated

      // Inconsistent state: authenticated flag without any tokens.
      // Can happen if localStorage is partially corrupted.
      // Force logout to get back to a clean state.
      if (currentAuth && !currentToken) {
        await useAuthStore.getState().logout()
        setIsVerifying(false)
        return
      }

      // Not authenticated and no token — nothing to verify
      if (!currentToken) {
        setIsVerifying(false)
        return
      }

      if (!isTokenExpired(currentToken) && !isTokenExpiringSoon(currentToken)) {
        // Token is valid and not expiring soon — no refresh needed
        setIsVerifying(false)
        return
      }

      // Token is expired or expiring soon — try to refresh
      if (refreshToken) {
        await refreshAuth()
      }
      setIsVerifying(false)
    }

    verify()
  }, [hasHydrated, refreshAuth])

  // Show skeleton while hydrating or verifying
  if (!hasHydrated || isVerifying) {
    return (
      <div className="flex h-full items-center justify-center p-8 dark:bg-surface-950">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    const redirectParam = location.pathname !== '/'
      ? `?redirect=${encodeURIComponent(location.pathname)}`
      : ''
    return <Navigate to={`/login${redirectParam}`} replace />
  }

  // Force password change if required
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  // Route guard por permisos: un usuario con :own sólo puede acceder a sus
  // secciones permitidas → lo mandamos a su vista por defecto.
  if (isScopedUser && !isScopedAllowed(location.pathname)) {
    return <Navigate to="/crm/files" replace />
  }

  return <DefaultLayout />
}
